// src/pages/access/SelfRegisteredSection.tsx — Phase 10 / Feature 4.
//
// Third pending sub-section on the User Access "Pending" tab: people who
// hit "Register" on the sign-in page but weren't in approved_users.
// Backend (75842b0) records them in hub_pending_registrations and exposes:
//
//   GET    /api/access/registrations                  → list (admin-only)
//   POST   /api/access/registrations/:id/approve      → provision + email PIN
//   DELETE /api/access/registrations/:id              → hard delete + audit
//
// The Approve modal collects the minimum required to provision: first +
// last name (the registrant only provided email), a department multi-
// select sourced from the new /api/access/departments endpoint (Feature
// 5b — passed in from the parent so we don't re-fetch), and an optional
// Admin checkbox. On 200 the parent flashes a confirmation and refreshes
// both lists; on 409 (already approved) the server message surfaces
// inline so the admin can react.

import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiError } from "../../api";
import { C, F } from "../../theme";
import { Button, Card, Eyebrow, Stamp } from "../../components/ui";

interface PendingRegistration {
  id: number;
  email: string;
  requested_at: string;
  last_requested_at: string;
  request_count: number;
  requested_from_ip: string | null;
}

interface PendingRegistrationsResponse {
  pending: PendingRegistration[];
}

interface DeptOption {
  id: number;
  name: string;
}

export interface SelfRegisteredSectionProps {
  /** Department options for the Approve modal's multi-select. Sourced from
   *  /api/access/departments by the parent. Empty array is rendered as
   *  "No departments — add one first" so the admin doesn't blow up the
   *  POST with an empty `departments` array (server requires non-empty). */
  departments: DeptOption[];
  /** Called after Approve or Reject succeeds so the parent can flash a
   *  message and refresh the user list. */
  onChanged: (message: string) => void;
  /** Phase 11 / Q5 — called after a successful Approve with the email of
   *  the newly-provisioned user. The parent uses it to navigate straight
   *  to /access/users/:id (looking up the id from the refreshed user
   *  list). Called BEFORE onChanged. */
  onApproved?: (email: string) => void;
}

export function SelfRegisteredSection({
  departments,
  onChanged,
  onApproved,
}: SelfRegisteredSectionProps) {
  const [pending, setPending] = useState<PendingRegistration[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState<PendingRegistration | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch<PendingRegistrationsResponse>(
        "/api/access/registrations",
      );
      setPending(res.pending);
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : "Couldn't load self-registrations.",
      );
      setPending([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const reject = useCallback(
    async (row: PendingRegistration) => {
      if (
        !window.confirm(
          `Reject the registration request from ${row.email}? They won't be notified.`,
        )
      ) {
        return;
      }
      setBusyId(row.id);
      setError(null);
      try {
        await apiFetch(`/api/access/registrations/${row.id}`, {
          method: "DELETE",
        });
        onChanged(`Rejected ${row.email}.`);
        await load();
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "Reject failed.");
      } finally {
        setBusyId(null);
      }
    },
    [load, onChanged],
  );

  // Hide the section entirely until we know whether there's anything to
  // show — keeps the Pending tab visually clean when there are no
  // self-registrations.
  if (pending === null && error === null) return null;
  if (pending !== null && pending.length === 0 && error === null) return null;

  return (
    <>
      <Card padded={false}>
        <div style={{ padding: "12px 16px 8px" }}>
          <Eyebrow>Self-registered — awaiting approval</Eyebrow>
          <div
            style={{
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 12,
              color: C.inkMute,
              marginTop: 4,
              lineHeight: 1.45,
            }}
          >
            People who hit Register on the sign-in page but weren&apos;t in
            the approved list. Approving provisions a PIN account and
            emails them their PIN.
          </div>
        </div>
        {error && (
          <div
            style={{
              margin: "0 16px 8px",
              padding: "8px 10px",
              background: C.signalBg,
              border: `1px solid rgba(184,58,43,0.22)`,
              borderRadius: 10,
              fontFamily: F.body,
              fontSize: 13,
              fontWeight: 700,
              color: C.signalDk,
            }}
          >
            {error}
          </div>
        )}
        {pending?.map((row, i) => (
          <SelfRegisteredRow
            key={row.id}
            row={row}
            isFirst={i === 0}
            busy={busyId === row.id}
            onApprove={() => setApproving(row)}
            onReject={() => void reject(row)}
          />
        ))}
      </Card>

      {approving && (
        <ApproveModal
          row={approving}
          departments={departments}
          onCancel={() => setApproving(null)}
          onApproved={(msg, email) => {
            setApproving(null);
            // Notify parent it can navigate to the new edit page first;
            // the parent owns the redirect so the section unmounts
            // cleanly rather than fetching its own list after navigating
            // away. If no navigation hook is provided, fall back to
            // just refreshing this section.
            onApproved?.(email);
            onChanged(msg);
            if (!onApproved) void load();
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function SelfRegisteredRow({
  row,
  isFirst,
  busy,
  onApprove,
  onReject,
}: {
  row: PendingRegistration;
  isFirst: boolean;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const requested = formatRelative(row.last_requested_at);
  return (
    <div
      style={{
        padding: "14px 16px",
        borderTop: isFirst ? "none" : `1px solid ${C.hairlineLt}`,
      }}
    >
      {/* Phase 11 / Q5 — whole row click opens the Approve modal. Same
          gesture as the PIN-invite cards on PendingTab, except those
          go straight to /access/users/:id because the user already
          has a hub_users row. Self-registered users don't yet, so
          Approve is the only meaningful action. The explicit
          Approve/Reject buttons below stay (with stopPropagation) so
          Reject doesn't accidentally open the modal. */}
      <div
        role="button"
        tabIndex={0}
        onClick={busy ? undefined : onApprove}
        onKeyDown={(e) => {
          if (busy) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onApprove();
          }
        }}
        style={{
          cursor: busy ? "default" : "pointer",
          padding: "4px 0",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "baseline" }}>
          <span
            style={{
              fontFamily: F.serif,
              fontSize: 16,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: -0.2,
              wordBreak: "break-word",
              minWidth: 0,
            }}
          >
            {row.email}
          </span>
          <Stamp tone="paper">Self-registered</Stamp>
          {row.request_count > 1 && (
            <Stamp tone="brass">×{row.request_count} attempts</Stamp>
          )}
        </div>
        <div
          style={{
            fontFamily: F.body,
            fontSize: 12,
            color: C.inkSoft,
            fontWeight: 600,
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          Requested {requested}
          {row.requested_from_ip ? ` · from ${row.requested_from_ip}` : ""}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <Button
          variant="primary"
          onClick={(e) => {
            e.stopPropagation();
            onApprove();
          }}
          disabled={busy}
        >
          Approve
        </Button>
        <Button
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onReject();
          }}
          disabled={busy}
        >
          {busy ? "Working…" : "Reject"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Approve modal
// ---------------------------------------------------------------------------

interface ApproveResponse {
  ok: true;
  email: string;
  emailed: boolean;
}

function ApproveModal({
  row,
  departments,
  onCancel,
  onApproved,
}: {
  row: PendingRegistration;
  departments: DeptOption[];
  onCancel: () => void;
  onApproved: (message: string, email: string) => void;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [selectedDepts, setSelectedDepts] = useState<Set<string>>(new Set());
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const trimmedFirst = firstName.trim();
  const trimmedLast = lastName.trim();
  const canSubmit =
    !busy &&
    trimmedFirst.length > 0 &&
    trimmedLast.length > 0 &&
    selectedDepts.size > 0 &&
    departments.length > 0;

  const toggleDept = useCallback((name: string) => {
    setSelectedDepts((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const submit = useCallback(async () => {
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await apiFetch<ApproveResponse>(
        `/api/access/registrations/${row.id}/approve`,
        {
          method: "POST",
          body: JSON.stringify({
            first_name: trimmedFirst,
            last_name: trimmedLast,
            departments: Array.from(selectedDepts),
            is_admin: isAdmin,
          }),
        },
      );
      onApproved(
        res.emailed
          ? `Approved ${res.email}; PIN emailed.`
          : `Approved ${res.email}; email failed (use Reveal PIN from their record).`,
        res.email,
      );
    } catch (e) {
      if (e instanceof ApiError) {
        setErr(e.message || `Approve failed (${e.status}).`);
      } else {
        setErr((e as Error)?.message ?? "Approve failed.");
      }
    } finally {
      setBusy(false);
    }
  }, [canSubmit, isAdmin, onApproved, row.id, selectedDepts, trimmedFirst, trimmedLast]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="approve-title"
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(43, 32, 20, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 200,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.card,
          borderRadius: 16,
          border: `1px solid ${C.hairline}`,
          boxShadow: C.shadowL,
          width: "100%",
          maxWidth: 480,
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          padding: 18,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          id="approve-title"
          style={{
            fontFamily: F.serif,
            fontSize: 18,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: -0.2,
          }}
        >
          Approve registration
        </div>
        <div
          style={{
            fontFamily: F.body,
            fontSize: 13,
            color: C.inkSoft,
            background: C.paperWarm,
            border: `1px solid ${C.hairlineLt}`,
            borderRadius: 10,
            padding: "8px 10px",
            wordBreak: "break-word",
          }}
        >
          <b>{row.email}</b>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <NameField
            label="First name"
            value={firstName}
            onChange={setFirstName}
            disabled={busy}
            autoFocus
          />
          <NameField
            label="Last name"
            value={lastName}
            onChange={setLastName}
            disabled={busy}
          />
        </div>

        <div>
          <div
            style={{
              fontFamily: F.body,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              color: C.inkMute,
              marginBottom: 6,
            }}
          >
            Departments (at least one)
          </div>
          {departments.length === 0 ? (
            <div
              style={{
                fontFamily: F.serif,
                fontStyle: "italic",
                fontSize: 13,
                color: C.signalDk,
                background: C.signalBg,
                border: `1px solid rgba(184,58,43,0.22)`,
                borderRadius: 10,
                padding: "8px 10px",
              }}
            >
              No departments configured yet. Add one via the &ldquo;+ New
              Department&rdquo; button on the Users tab first.
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {departments.map((d) => {
                const on = selectedDepts.has(d.name);
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => toggleDept(d.name)}
                    disabled={busy}
                    style={{
                      padding: "6px 11px",
                      borderRadius: 999,
                      border: on
                        ? `1.5px solid ${C.heatherDk}`
                        : `1px solid ${C.hairline}`,
                      background: on ? C.heatherBg : C.card,
                      color: on ? C.heatherDk : C.inkSoft,
                      fontFamily: F.body,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: busy ? "not-allowed" : "pointer",
                    }}
                  >
                    {d.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 10px",
            background: C.paperSoft,
            border: `1px solid ${C.hairlineLt}`,
            borderRadius: 10,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={isAdmin}
            onChange={(e) => setIsAdmin(e.target.checked)}
            disabled={busy}
          />
          <span
            style={{
              fontFamily: F.body,
              fontSize: 13,
              fontWeight: 700,
              color: C.ink,
            }}
          >
            Grant Admin
          </span>
          <span
            style={{
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 12,
              color: C.inkMute,
            }}
          >
            (rare — leave unchecked unless intentional)
          </span>
        </label>

        {err && (
          <div
            role="alert"
            style={{
              fontFamily: F.body,
              fontSize: 12,
              color: C.signalDk,
              fontWeight: 700,
              background: C.signalBg,
              border: `1px solid rgba(184,58,43,0.22)`,
              padding: "8px 10px",
              borderRadius: 10,
            }}
          >
            {err}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 4,
          }}
        >
          <Button variant="soft" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!canSubmit}
          >
            {busy ? "Approving…" : "Approve & email PIN"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function NameField({
  label,
  value,
  onChange,
  disabled,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        fontFamily: F.body,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        color: C.inkMute,
      }}
    >
      {label}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, 80))}
        disabled={disabled}
        autoFocus={autoFocus}
        style={{
          padding: "9px 11px",
          border: `1px solid ${C.hairline}`,
          borderRadius: 10,
          fontFamily: F.body,
          fontSize: 14,
          fontWeight: 600,
          color: C.ink,
          background: C.paperSoft,
          outline: "none",
        }}
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMin = Math.round((Date.now() - then) / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const h = Math.round(diffMin / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 14) return `${d}d ago`;
  const w = Math.round(d / 7);
  return `${w}w ago`;
}
